import { useContext } from "react";
import { Button, Input, Modal, Select, Table, Tag, Typography } from "antd";
import {
    PlusOutlined,
    SearchOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";

import useRoles from "./useRoles";
import PageHeader from "../../components/PageHeader";
import ActiveFiltersBar from "../../components/ActiveFiltersBar";
import TableCard from "../../components/TableCard";
import TableDicaHint from "../../components/TableDicaHint";
import TablePagination from "../../components/TablePagination";
import { ThemeContext } from "../../contexts/ThemeContext";
import { poeValorComPontosEmMil } from "../../utils/Conversores";

const { Paragraph } = Typography;

export default function Roles() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        isLoading,
        isDeleteModalVisible,
        isLoadingDelete,
        roleBeingDeleted,
        columns,
        tableData,
        filteredData,
        searchText,
        setSearchText,
        filterWithPermissions,
        setFilterWithPermissions,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleClickCreateNewRole,
        handleClickEditRole,
        handleConfirmDeleteRole,
        handleCancelDeleteRole,
        handleClearAllFilters,
    } = useRoles();

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <PageHeader
                icon={<SafetyCertificateOutlined />}
                iconBg="linear-gradient(135deg, #722ed1 0%, #1677ff 100%)"
                title="Perfis de Acesso"
                tag={<Tag color="purple">{poeValorComPontosEmMil(tableData.length)} cadastrados</Tag>}
                subtitle="Gerencie os níveis de permissão e perfis de acesso dos usuários ao painel administrativo"
                actions={
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleClickCreateNewRole}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontWeight: 600,
                                height: 38,
                                borderRadius: 8,
                            }}
                        >
                            Novo Perfil
                        </Button>
                    </div>
                }
            />

            {/* Filter Bar / Controls */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    borderRadius: 10,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    gap: 14,
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 240, maxWidth: 380 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Pesquisar Perfil
                    </span>
                    <Input
                        placeholder="Buscar por nome ou descrição..."
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setCurrentPage(1);
                        }}
                        allowClear
                        style={{ borderRadius: 8, height: 36 }}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4, width: 220 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? "#d4d4d4" : "#475569" }}>
                        Permissões Atribuídas
                    </span>
                    <Select
                        value={filterWithPermissions}
                        onChange={(val) => {
                            setFilterWithPermissions(val);
                            setCurrentPage(1);
                        }}
                        style={{ width: "100%", height: 36 }}
                        options={[
                            { value: "ALL", label: "Todas as Permissões" },
                            { value: "WITH", label: "Com permissões" },
                            { value: "WITHOUT", label: "Sem permissões" },
                        ]}
                    />
                </div>
            </div>

            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            {/* Hint & Pagination (Top) */}
            <TableDicaHint text="Clique em uma linha para editar o perfil ou passe o cursor sobre a tag de permissões para inspecioná-las." />

            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="perfis"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            <TableCard>
                <Table
                    columns={columns as any}
                    dataSource={filteredData.slice(
                        (currentPage - 1) * pageSize,
                        currentPage * pageSize
                    )}
                    rowKey="key"
                    loading={isLoading}
                    pagination={false}
                    className={`modern-data-table ${isDark ? "modern-data-table-dark" : ""}`}
                    scroll={{ x: 650 }}
                    onRow={(record) => ({
                        onClick: () => handleClickEditRole(record),
                        style: { cursor: "pointer" },
                    })}
                />
            </TableCard>

            <TablePagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredData.length}
                itemName="perfis"
                pageSizeOptions={["10", "25", "50", "100"]}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                }}
            />

            <Modal
                open={isDeleteModalVisible}
                onOk={handleConfirmDeleteRole}
                onCancel={handleCancelDeleteRole}
                confirmLoading={isLoadingDelete}
                okText="Sim, Deletar"
                cancelText="Cancelar"
                okType="danger"
                okButtonProps={{ loading: isLoadingDelete }}
                cancelButtonProps={{ disabled: isLoadingDelete }}
                centered
                title="Confirmar Exclusão de Perfil"
            >
                <Paragraph style={{ marginTop: 12 }}>
                    Deseja realmente deletar o perfil de acesso{" "}
                    <strong>"{roleBeingDeleted?.name}"</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#1f1315" : "#fff1f0",
                        border: `1px solid ${isDark ? "#431418" : "#ffa39e"}`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: isDark ? "#ffa39e" : "#cf1322",
                    }}
                >
                    Esta ação removerá este perfil permanentemente. Usuários que utilizavam este perfil perderão os acessos vinculados.
                </div>
            </Modal>
        </div>
    );
}
